/**
 * Estimates and Invoices as their own tabs.
 *
 * They used to be two sections inside the single blended "Work at this
 * site" card, which meant the only way to answer "does this location owe
 * us anything" was to read past a jobs list. Now each is a real list.
 *
 * ── The permission seam, stated rather than discovered ────────────────
 *
 * `/api/commercial/documents` sits behind `invoices.view` while the rest
 * of the location page is plain auth. A CSR without that permission would
 * otherwise open these tabs and get a red error toast over an empty
 * table, which reads as "the page is broken" rather than "this isn't
 * yours to see". So the tabs render a plain sentence and never fire the
 * query. The server check is still the real one -- this is courtesy, not
 * security.
 *
 * The Estimates tab keeps the inline Approve / Create-job flow and its
 * forced match review on NEEDS_REVIEW, because that flow is the reason
 * the office works estimates from the location page at all. It lives in
 * `work-at-site.tsx` and is imported, not reimplemented.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { SortScopeNote } from "@/components/ui/sort-scope-note";
import { dateSortValue } from "@/lib/table-sort";
import { cn, formatDate, formatDollars, statusColor } from "@/lib/utils";
import { CheckSquare, FileText, Mail, Plus, Receipt } from "lucide-react";
import { newEstimateHref } from "../location-page-v2";
import { OpenEstimatesList } from "./open-estimates";
import { EstimateBatchBar, EstimateSelectAllBar } from "@/pages/customers/estimate-batch/estimate-batch-bar";
import { EstimateSendDialog } from "@/pages/customers/estimate-batch/estimate-send-dialog";
import {
    isSendable,
    selectionScopeLabel as estimateScopeLabel,
    unsendableReason,
} from "@/pages/customers/estimate-batch/estimate-batch-copy";
import type { SiteContext } from "./types";

export interface DocRow {
    id: string;
    doc_type: "estimate" | "invoice";
    doc_number: string;
    customer_name: string;
    status: string;
    total_cents: number;
    balance_cents?: number;
    issued_at: string;
    created_at: string;
}

function useSiteDocs(siteId: string, docType: "estimate" | "invoice", enabled: boolean) {
    return useQuery({
        queryKey: ["site-commercial-docs", siteId, docType],
        queryFn: () =>
            api.get<{ rows: DocRow[]; total: number }>(
                `/api/commercial/documents?siteId=${siteId}&doc_type=${docType}&limit=200`,
            ),
        enabled: enabled && !!siteId,
    });
}

function NoPermission({ what }: { what: string }) {
    return (
        <p className="text-sm text-foreground/70">
            You don&apos;t have access to {what} on this account. Ask an admin for the &ldquo;See invoices&rdquo;
            permission if you need it.
        </p>
    );
}

/**
 * Every header on these tables sorts. The accessors read the raw column, not
 * the rendered cell: a sort on the formatted text would order $90 above
 * $1,200 and "Aug 2026" above "Jan 2027". Money arrives here in CENTS, so it
 * sorts as an integer with no rounding in the middle, and doc numbers sort
 * digit-aware so INV-9 lands ahead of INV-10 (see `lib/table-sort`).
 *
 * Exported, and built by a function rather than inlined in the component, so
 * the accessors are testable without mounting the page.
 */
export function docColumns(showBalance: boolean): Column<DocRow>[] {
    return [
        {
            key: "doc_number",
            header: "#",
            sortable: true,
            sortAccessor: (r) => r.doc_number,
            render: (r) => (
                <Link
                    to={`/commercial/${r.id}?type=${r.doc_type}`}
                    className="font-mono text-xs font-medium text-primary hover:underline"
                >
                    {r.doc_number}
                </Link>
            ),
        },
        {
            key: "status",
            header: "Status",
            sortable: true,
            sortAccessor: (r) => r.status,
            render: (r) => (
                <Badge variant="outline" className={cn("text-[10px]", statusColor(r.status))}>
                    {r.status?.replace(/_/g, " ")}
                </Badge>
            ),
        },
        {
            key: "issued_at",
            header: "Issued",
            sortable: true,
            firstSortDir: "desc",
            // Never issued shows "—" and sorts last, not as 1970.
            sortAccessor: (r) => dateSortValue(r.issued_at),
            render: (r) => (
                <span className="text-xs tabular-nums text-foreground/80">
                    {r.issued_at ? formatDate(r.issued_at) : "—"}
                </span>
            ),
        },
        {
            key: "total_cents",
            header: "Total",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => r.total_cents,
            render: (r) => <span className="text-sm tabular-nums">{formatDollars(r.total_cents / 100)}</span>,
        },
        ...(showBalance
            ? [
                  {
                      key: "balance_cents",
                      header: "Balance",
                      sortable: true,
                      firstSortDir: "desc" as const,
                      sortAccessor: (r: DocRow) => r.balance_cents ?? 0,
                      render: (r: DocRow) => {
                          const bal = (r.balance_cents ?? 0) / 100;
                          return (
                              <span className={cn("text-sm tabular-nums", bal > 0 && "font-semibold text-warning")}>
                                  {formatDollars(bal)}
                              </span>
                          );
                      },
                  } satisfies Column<DocRow>,
              ]
            : []),
    ];
}

function DocsTable({
    rows,
    total,
    loading,
    emptyMessage,
    showBalance,
    noun,
    selectable,
    selectedIds,
    onSelectionChange,
}: {
    rows: DocRow[];
    total: number | undefined;
    loading: boolean;
    emptyMessage: string;
    showBalance: boolean;
    noun: string;
    /** #1349 — opt-in; the Invoices section passes none and renders as before. */
    selectable?: boolean;
    selectedIds?: string[];
    onSelectionChange?: (ids: string[]) => void;
}) {
    const columns = useMemo(() => docColumns(showBalance), [showBalance]);
    return (
        <>
            <DataTable
                columns={columns}
                data={rows}
                keyField="id"
                // One saved sort per kind of document, shared by every location.
                sortId={`site.${noun.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                loading={loading}
                emptyMessage={emptyMessage}
                selectable={selectable}
                selectedIds={selectedIds}
                onSelectionChange={(ids) => onSelectionChange?.(ids.map(String))}
                rowSelectable={(r) => isSendable(r)}
                rowUnselectableReason={(r) => unsendableReason(r)}
                totalRows={total}
            />
            <SortScopeNote loaded={rows.length} total={total} noun={noun} />
        </>
    );
}

export function EstimatesSection({ site }: { site: SiteContext }) {
    const { isAdmin, can } = useAuth();
    const allowed = isAdmin || can("invoices.view");
    const q = useSiteDocs(site.siteId, "estimate", allowed);
    const rows = q.data?.rows ?? [];

    // #1349 — the same select-and-email-as-one-zip flow the customer page's
    // Estimates tab has, scoped to this one location. The components and the
    // rules are imported, not re-implemented: two copies of "which statuses
    // can be sent" is how the two screens start disagreeing.
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [sendOpen, setSendOpen] = useState(false);
    const sendable = useMemo(() => rows.filter((r) => isSendable(r)), [rows]);
    const selectedTotal = useMemo(() => {
        const picked = new Set(selected);
        return rows.filter((r) => picked.has(r.id)).reduce((n, r) => n + (r.total_cents ?? 0), 0) / 100;
    }, [rows, selected]);

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="h-4 w-4" /> Estimates ({allowed ? rows.length : 0})
                </CardTitle>
                <div className="flex items-center gap-2">
                    {allowed && (
                        <Button
                            size="sm"
                            variant={selectMode ? "secondary" : "outline"}
                            data-testid="location-estimates-select-toggle"
                            onClick={() => {
                                setSelectMode((on) => !on);
                                setSelected([]);
                            }}
                        >
                            <CheckSquare className="mr-1 h-3.5 w-3.5" />
                            {selectMode ? "Done selecting" : "Select"}
                        </Button>
                    )}
                    {selectMode && selected.length > 0 && (
                        <Button size="sm" onClick={() => setSendOpen(true)}>
                            <Mail className="mr-1 h-3.5 w-3.5" /> Email {selected.length} estimate
                            {selected.length === 1 ? "" : "s"}
                        </Button>
                    )}
                    <Link
                        to={newEstimateHref(site.customerId, site.siteId)}
                        className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
                    >
                        <Plus className="mr-1 h-4 w-4" /> New estimate
                    </Link>
                </div>
            </CardHeader>
            <CardContent>
                {/* Open estimates first, with the inline Approve /
                    Create-job actions — the thing the office came here to
                    DO. Needs no `invoices.view`: it reads /work-summary,
                    the same plain-auth endpoint the page already uses. */}
                <div className="mb-5 space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground/70">Open estimates</h3>
                    <OpenEstimatesList siteId={site.siteId} />
                </div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground/70">All estimates</h3>
                {!allowed ? (
                    <NoPermission what="the full estimate history" />
                ) : (
                    <>
                        {selectMode && (
                            <EstimateSelectAllBar
                                sendableCount={sendable.length}
                                blockedCount={rows.length - sendable.length}
                                selectedCount={selected.length}
                                allSelected={sendable.length > 0 && selected.length === sendable.length}
                                onSelectAll={(checked) => setSelected(checked ? sendable.map((r) => r.id) : [])}
                                onClear={() => setSelected([])}
                            />
                        )}
                        <DocsTable
                            rows={rows}
                            total={q.data?.total}
                            loading={q.isLoading}
                            emptyMessage="No estimates for this location yet."
                            showBalance={false}
                            noun="estimates"
                            selectable={selectMode}
                            selectedIds={selected}
                            onSelectionChange={setSelected}
                        />
                        {selectMode && (
                            <EstimateBatchBar
                                selectedCount={selected.length}
                                selectedTotal={selectedTotal}
                                scopeLabel={estimateScopeLabel(rows.length, q.data?.total, sendable.length)}
                                onClear={() => setSelected([])}
                                onEmail={() => setSendOpen(true)}
                            />
                        )}
                        <EstimateSendDialog
                            customerId={site.customerId}
                            customerName={null}
                            estimateIds={selected}
                            open={sendOpen}
                            onClose={() => setSendOpen(false)}
                            onSent={() => {
                                setSelected([]);
                                setSelectMode(false);
                                void q.refetch();
                            }}
                        />
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export function InvoicesSection({ site }: { site: SiteContext }) {
    const { isAdmin, can } = useAuth();
    const allowed = isAdmin || can("invoices.view");
    const q = useSiteDocs(site.siteId, "invoice", allowed);
    const rows = q.data?.rows ?? [];
    const outstanding = rows.reduce((n, r) => n + (r.balance_cents ?? 0), 0);

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <Receipt className="h-4 w-4" /> Invoices ({allowed ? rows.length : 0})
                </CardTitle>
                {allowed && outstanding > 0 && (
                    <span className="text-sm font-semibold tabular-nums text-warning">
                        {formatDollars(outstanding / 100)} outstanding
                    </span>
                )}
            </CardHeader>
            <CardContent>
                {!allowed ? (
                    <NoPermission what="invoices" />
                ) : (
                    <DocsTable
                        rows={rows}
                        total={q.data?.total}
                        loading={q.isLoading}
                        emptyMessage="No invoices for this location yet."
                        showBalance
                        noun="invoices"
                    />
                )}
            </CardContent>
        </Card>
    );
}
